import { useMutation, useQuery } from '@tanstack/react-query';
import { showToast } from 'app/shared/util/toast.util';
import { queryClient } from 'app/app';
import { PaginationState } from 'app/shared/pagination/pagination.model';
import {
  deleteFinalReport,
  deleteWrittenStatement,
  existsReportsOrStatementsByBuildingDiaryCode,
  fetchFinalReports,
  fetchReportsAndStatements,
  fetchWrittenStatements,
  getFinalReport,
  getTechnicalInspection,
  getWrittenStatement,
  saveFinalReport,
  saveTechnicalInspection,
  saveWrittenStatement,
} from 'app/edozvola/cases/case-technical-inspection/service/case-technical-inspection.service';
import { toast } from 'react-toastify';
import success = toast.success;
import error = toast.error;
import { CaseTechnicalInspectionModel } from '../model/case-technical-inspection.model';
import {messages, TABLE_QUERY_KEYS} from "app/config/constants";

export const saveFinalReportQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (finalReportOfSupervisingEngineerModel: any) => {
      return saveFinalReport(idCase, finalReportOfSupervisingEngineerModel);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.FINAL_REPORTS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchFinalReportsTableQuery = (pagination: PaginationState, uuid: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.FINAL_REPORTS_TABLE, { ...pagination }, uuid],
    queryFn: ({ signal }) => fetchFinalReports({ signal, pagination, uuid }),
  });
};


export const fetchFinalReportQuery = (idFinalReport: string) => {
  return useQuery({
    queryKey: ['final-report', idFinalReport],
    queryFn: () => getFinalReport(idFinalReport),
    enabled: !!idFinalReport,
  });
};

export const deleteFinalReportQuery = () => {
  return useMutation({
    mutationFn: (idFinalReport: any) => {
      return deleteFinalReport(idFinalReport);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.FINAL_REPORTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const saveWrittenStatementQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (writtenStatementModel: any) => {
      return saveWrittenStatement(idCase, writtenStatementModel);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.WRITTEN_STATEMENTS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchWrittenStatementsTableQuery = (pagination: PaginationState, uuid: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.WRITTEN_STATEMENTS_TABLE, { ...pagination }, uuid],
    queryFn: ({ signal }) => fetchWrittenStatements({ signal, pagination, uuid }),
  });
};


export const fetchWrittenStatementQuery = (idWrittenStatement: string) => {
  return useQuery({
    queryKey: ['written-statement', idWrittenStatement],
    queryFn: () => getWrittenStatement(idWrittenStatement),
    enabled: !!idWrittenStatement,
  });
};

export const deleteWrittenStatementQuery = () => {
  return useMutation({
    mutationFn: (idWrittenStatement: any) => {
      return deleteWrittenStatement(idWrittenStatement);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.WRITTEN_STATEMENTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const getTechnicalInspectionQuery = (uuid: string) => {
  return useQuery({
    queryKey: ['technical-inspection', uuid],
    queryFn: ({ signal }) => getTechnicalInspection(uuid),
  });
};

export const saveTechnicalInspectionQuery = (uuid: string, resetForm) => {
  return useMutation({
    mutationFn: (technicalInspection: CaseTechnicalInspectionModel) => saveTechnicalInspection(uuid, technicalInspection),
    onSuccess: (data, variables, context) => {
      showToast(success, messages.DATA_SAVED);
      resetForm({ values: variables });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchReportsAndStatementsMutation = (uuid: string, setBuildingDiaryCodeToFetchReports) => {
  return useMutation({
    mutationFn: (buildingDiaryCode: string) => fetchReportsAndStatements(uuid, buildingDiaryCode),
    onSuccess: (data, variables, context) => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.WRITTEN_STATEMENTS_TABLE] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.FINAL_REPORTS_TABLE] });
      setBuildingDiaryCodeToFetchReports('');
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const existsReportsOrStatementsByBuildingDiaryCodeMutation = (uuid: string, setConfirmationModal, fetchReports) => {
  return useMutation({
    mutationFn: (buildingDiaryCode: string) => existsReportsOrStatementsByBuildingDiaryCode(uuid, buildingDiaryCode),
    onSuccess: (data, variables, context) => {
      if (data) {
        setConfirmationModal(true);
      } else {
        fetchReports(variables);
      }
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

